import React, { useState, useEffect, useMemo } from 'react';
import Icon from '../../../../components/AppIcon';
import {
  setScheduledShift,
  createOverride,
  createRecurringRule,
  updateRecurringRule,
  createShiftSet,
  deleteShiftSet,
  getShiftSet,
  updateShiftSet,
  assignUsersToShiftSet,
  timeToMinutes,
} from '../../../../api/Attendance/scheduling';
import { FetchUsers } from '../../../../api/General/account';
import { useSelector } from 'react-redux';
import Button from '../../../../components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogTitle, DialogFooter } from '../../../../components/ui/Dialog';
import { Checkbox } from '../../../../components/ui/Checkbox';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';
import { formatErrorMessage, notifyError } from '../../../../utils/errors';
import { appDateTime, isoDateToDisplay } from '../../../../utils/appDateFormat';
import { DateField, TimeField } from '../../../../components/dateFields';
import { useTranslation } from 'react-i18next';

const CreateScheduleDrawer = ({ isOpen, onClose, onScheduleCreated, editingShift, templates, users, preSelectedUser }) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogData, setErrorDialogData] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(''); // '' = all
  
  // Get current user's role and company from Redux store
  const userRole = useSelector((state) => state.user.role);
  const userCompanyId = useSelector((state) => state.user.companyId);
  const isManager = useSelector((state) => state.user.isManager);
  const [formData, setFormData] = useState({
    type: 'single', // 'single' or 'recurring'
    date: '',
    startTime: '',
    endTime: '',
    breakStartTime: '',
    breakEndTime: '',
    breakDuration: null,
    userIds: [], // Changed from userId to userIds for multi-select
    templateId: '',
    createNewTemplate: true,
    newTemplateName: '',
    newTemplateDescription: '',
    addRecurrence: false,
    recurrenceRule: {
      frequency: 'WEEKLY',
      days: [],
      interval: 1,
      endDate: ''
    },
    reason: ''
  });
  const [originalRuleId, setOriginalRuleId] = useState(null);
  const [originalTemplateName, setOriginalTemplateName] = useState('');
  const [originalTemplateDescription, setOriginalTemplateDescription] = useState('');
  const [originalSetId, setOriginalSetId] = useState(null);

  // When opening the drawer for a NEW schedule (not editing), ensure stale edit state is cleared.
  // Otherwise we can accidentally PUT-update an existing rule from a different shift set.
  useEffect(() => {
    if (isOpen && !editingShift) {
      setError(null);
      setErrorDialogOpen(false);
      setErrorDialogData(null);
      setOriginalRuleId(null);
      setOriginalTemplateName('');
      setOriginalTemplateDescription('');
      setOriginalSetId(null);
      setFormData({
        type: 'single', // 'single' or 'recurring'
        date: '',
        startTime: '',
        endTime: '',
        breakStartTime: '',
        breakEndTime: '',
        breakDuration: null,
        userIds: preSelectedUser ? [preSelectedUser] : [],
        templateId: '',
        createNewTemplate: true,
        newTemplateName: '',
        newTemplateDescription: '',
        addRecurrence: false,
        recurrenceRule: {
          frequency: 'WEEKLY',
          days: [],
          interval: 1,
          endDate: ''
        },
        reason: ''
      });
    }
  }, [isOpen, editingShift, preSelectedUser]);

  const minutesToTimeString = (mins) => {
    if (typeof mins !== 'number' || !Number.isFinite(mins)) return '';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  const parseRecurrenceDaysFromRRule = (rRule) => {
    if (typeof rRule !== 'string' || rRule.trim().length === 0) return [];
    const byDayMatch = rRule.match(/BYDAY=([^;]+)/i);
    if (!byDayMatch || !byDayMatch[1]) return [];
    return byDayMatch[1]
      .split(',')
      .map((s) => (s || '').trim().toUpperCase())
      .filter(Boolean);
  };

  const parseRecurrenceIntervalFromRRule = (rRule) => {
    if (typeof rRule !== 'string' || rRule.trim().length === 0) return 1;
    const intervalMatch = rRule.match(/INTERVAL=(\d+)/i);
    if (!intervalMatch) return 1;
    const val = parseInt(intervalMatch[1], 10);
    return Number.isFinite(val) && val > 0 ? val : 1;
  };

  const applyTemplateToForm = (template) => {
    if (!template) return;

    const rule = Array.isArray(template?.recurringRules) ? template.recurringRules[0] : null;
    const recurrenceDays = parseRecurrenceDaysFromRRule(rule?.rRule);
    const recurrenceInterval = parseRecurrenceIntervalFromRRule(rule?.rRule);
    const rawEnd = typeof rule?.endDateUtc === 'string' ? rule.endDateUtc.split('T')[0] : '';
    const recurrenceEndDate = rawEnd === '9999-12-31' ? '' : rawEnd;

    const assignedIds = Array.isArray(template?.assignedUserIds)
      ? template.assignedUserIds
      : (Array.isArray(template?.assignedUsers) ? template.assignedUsers.map(u => u?.id).filter(Boolean) : []);

    const selectedUsers = (assignedIds || [])
      .map((id) => {
        const found = availableUsers.find((au) => String(au?.id) === String(id));
        const fallback = template?.assignedUsers?.find((u) => String(u?.id) === String(id));
        return found || (fallback ? { id: fallback.id, name: fallback.name || fallback.fullName || String(id), fullName: fallback.fullName } : { id, name: String(id) });
      })
      .filter((u) => u != null);

    setFormData((prev) => ({
      ...prev,
      // Selecting a template implies we are using an existing template.
      createNewTemplate: false,
      templateId: template?.id?.toString() || '',
      // Populate editable title/description for the selected template.
      newTemplateName: template?.name || '',
      newTemplateDescription: template?.description || '',
      // Populate schedule fields from the template's first recurring rule (best-effort).
      startTime: rule ? minutesToTimeString(rule.startTimeOfDayMinutes) : prev.startTime,
      endTime: rule ? minutesToTimeString(rule.endTimeOfDayMinutes) : prev.endTime,
      breakStartTime: rule ? minutesToTimeString(rule.breakStartTimeOfDayMinutes) : prev.breakStartTime,
      breakEndTime: rule ? minutesToTimeString(rule.breakEndTimeOfDayMinutes) : prev.breakEndTime,
      breakDuration: (rule?.breakDurationMinutes != null && Number(rule.breakDurationMinutes) > 0) ? Number(rule.breakDurationMinutes) : prev.breakDuration,
      userIds: selectedUsers.length > 0 ? selectedUsers : prev.userIds,
      recurrenceRule: {
        ...prev.recurrenceRule,
        days: recurrenceDays.length > 0 ? recurrenceDays : prev.recurrenceRule.days,
        endDate: recurrenceEndDate,
        interval: recurrenceInterval,
      },
    }));

    setOriginalTemplateName(template?.name || '');
    setOriginalTemplateDescription(template?.description || '');
    setOriginalSetId(template?.id || null);
    // When selecting an existing template, treat its first recurring rule as the one we're editing.
    // This prevents accidentally creating duplicate rules for the same template.
    setOriginalRuleId(
      rule?.id || template?.recurringRuleId || template?.linkedRecurringRuleId || null
    );
  };

  const extractDetailedError = (err) => {
    const result = err?.errors ? err : (err?.response?.data?.errors ? err.response.data : err);
    const errors = Array.isArray(result?.errors) ? result.errors : [];

    const descriptions = errors
      .map((e) => (typeof e?.description === 'string' ? e.description : (typeof e?.Description === 'string' ? e.Description : '')))
      .filter((s) => s && s.trim().length > 0);

    // Conflicts can be attached as errors[].details.conflicts (camelCase) or Details.Conflicts.
    let conflicts = null;
    for (const e of errors) {
      const details = e?.details || e?.Details;
      const c = details?.conflicts || details?.Conflicts;
      if (Array.isArray(c) && c.length > 0) {
        conflicts = c;
        break;
      }
    }

    const summary = descriptions[0] || formatErrorMessage(err, t('CouldntSaveSchedule'));
    return {
      summary,
      descriptions,
      conflicts,
      raw: result,
    };
  };

  // Load users when drawer opens
  useEffect(() => {
    if (isOpen) {
      loadAvailableUsers();
    }
  }, [isOpen, templates]);

  const loadAvailableUsers = async () => {
    try {
      setLoadingUsers(true);
      
      // Fetch all users for the company (backend handles role-based filtering)
      const usersResponse = await FetchUsers('Company', userCompanyId, false);
      
      if (usersResponse && usersResponse.payload) {
        // Map all users to our format (normalize department to string)
        const allUsers = usersResponse.payload.map(user => {
          const dept = typeof user?.departmentName === 'string'
            ? user.departmentName
            : (typeof user?.department === 'string' 
                ? user.department 
                : (user?.department?.name || user?.department?.title || ''));
          return {
            id: user.id,
            name: user.fullName || user.userName,
            fullName: user.fullName,
            department: dept,
          };
        });
        
        // Note: We don't filter users here anymore.
        // Users can be selected for single shifts regardless of template assignments.
        // Template conflict checking is done on the backend when creating/assigning templates.
        allUsers.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setAvailableUsers(allUsers);
      }
    } catch (err) {
      console.error('Error loading users:', err);
      const msg = formatErrorMessage(err, t('FailedToLoadUsers'));
      setError(msg);
      notifyError(err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const departmentOptions = useMemo(() => {
    const depts = (availableUsers || [])
      .map(u => {
        const dept = typeof u?.department === 'string'
          ? u.department
          : (u?.department?.name || u?.department?.title || '');
        return (dept || '').trim();
      })
      .filter(Boolean);
    return Array.from(new Set(depts)).sort((a, b) => a.localeCompare(b));
  }, [availableUsers]);

  const filteredUserOptions = useMemo(() => {
    const selected = Array.isArray(formData?.userIds) ? formData.userIds : [];
    const filtered = !selectedDepartment
      ? (availableUsers || [])
      : (availableUsers || []).filter(u => {
          const dept = (typeof u?.department === 'string'
            ? u.department
            : (u?.department?.name || u?.department?.title || ''));
          return (dept || '') === selectedDepartment;
        });

    // Ensure selected users remain in the options even if department filter excludes them.
    const byId = new Map();
    for (const u of filtered) byId.set(String(u?.id), u);
    for (const u of selected) {
      const key = String(u?.id);
      if (!byId.has(key)) byId.set(key, u);
    }
    return Array.from(byId.values());
  }, [availableUsers, formData?.userIds, selectedDepartment]);

  useEffect(() => {
    if (editingShift) {
      // We want the drawer to default to "Create New Template" only when creating a NEW schedule.
      // - Create button: editingShift is null (handled in the other effect)
      // - Plus button: editingShift is usually a small draft object like { date: 'YYYY-MM-DD' }
      // - Editing a calendar item or template: editingShift contains IDs/origin/template metadata
      const isEditingExisting = !!(
        editingShift?.id ||
        editingShift?.shiftSetId ||
        editingShift?.recurringShiftRuleId ||
        editingShift?.origin === 'Rule' ||
        editingShift?.origin === 'RecurringRule' ||
        editingShift?.ruleName
      );
      const shouldCreateNewTemplate = !isEditingExisting;

      // Get all assigned users from the consolidated shift
      let selectedUsers = [];
      
      if (editingShift.assignedUsers && editingShift.assignedUsers.length > 0) {
        // Map assigned users from shift to available users
        selectedUsers = editingShift.assignedUsers
          .map(u => {
            const foundUser = availableUsers.find(au => au.id === u.id);
            // If user not in availableUsers, create a placeholder
            return foundUser || { id: u.id, name: u.name || u.fullName || u.id, fullName: u.fullName };
          })
          .filter(u => u != null);
      } else if (editingShift.userId) {
        // Fallback to single user
        const selectedUser = availableUsers.find(u => u.id === editingShift?.userId);
        if (selectedUser) {
          selectedUsers = [selectedUser];
        }
      }
      
      // Find matching template based on shiftSetId, recurringShiftRuleId, or linkedRecurringRuleId
      let matchingTemplate = templates?.find(t => 
        t.id === editingShift?.shiftSetId || 
        t.recurringRuleId === editingShift?.recurringShiftRuleId ||
        t.linkedRecurringRuleId === editingShift?.recurringShiftRuleId
      );
      
      // Determine if this is a recurring shift and extract recurrence info
      const isRecurring = editingShift?.origin === 'Rule' || editingShift?.origin === 'RecurringRule';
      let recurrenceDays = [];
      let recurrenceEndDate = '';
      let recurrenceInterval = 1;
      
      // Try to get recurrence info from the matching template's recurring rules.
      // When a template has multiple rules (e.g. after a split), pick the specific
      // rule that matches the occurrence being edited, not just the first one.
      if (matchingTemplate && matchingTemplate.recurringRules?.length > 0) {
        const rule = (editingShift?.recurringShiftRuleId
          ? matchingTemplate.recurringRules.find(r => r.id === editingShift.recurringShiftRuleId)
          : null) || matchingTemplate.recurringRules[0];
        // Parse days from rRule like "FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR;INTERVAL=1"
        if (rule.rRule) {
          const byDayMatch = rule.rRule.match(/BYDAY=([^;]+)/);
          if (byDayMatch) {
            recurrenceDays = byDayMatch[1].split(',');
          }
          const intervalMatch = rule.rRule.match(/INTERVAL=(\d+)/);
          if (intervalMatch) {
            recurrenceInterval = parseInt(intervalMatch[1], 10);
          }
        }
        if (rule.endDateUtc) {
          const raw = rule.endDateUtc.split('T')[0];
          // Treat sentinel (open-ended) dates as "no end date" in UI.
          recurrenceEndDate = raw === '9999-12-31' ? '' : raw;
        }
      }
      
      setFormData((prev) => ({
        ...prev,
        type: isRecurring ? 'recurring' : 'single',
        date: editingShift?.date || '',
        startTime: editingShift?.startTime || '',
        endTime: editingShift?.endTime || '',
        breakStartTime: editingShift?.breakStartTime || '',
        breakEndTime: editingShift?.breakEndTime || '',
        breakDuration: Number.isFinite(editingShift?.breakDurationMinutes) ? editingShift.breakDurationMinutes : null,
        userIds: selectedUsers,
        templateId: matchingTemplate?.id?.toString() || editingShift?.shiftSetId?.toString() || '',
        createNewTemplate: shouldCreateNewTemplate,
        reason: editingShift?.reason || editingShift?.overrideReason || '',
        recurrenceRule: {
          ...prev.recurrenceRule,
          days: recurrenceDays,
          endDate: recurrenceEndDate,
          interval: recurrenceInterval,
        },
      }));
      // Only ever treat a recurring rule id as a rule id (never fall back to shift set id).
      setOriginalRuleId(
        editingShift?.recurringShiftRuleId ||
          matchingTemplate?.recurringRuleId ||
          matchingTemplate?.linkedRecurringRuleId ||
          matchingTemplate?.recurringRules?.[0]?.id ||
          null
      );
      setOriginalTemplateName(matchingTemplate?.name || editingShift?.ruleName || '');
      setOriginalTemplateDescription(matchingTemplate?.description || '');
      setOriginalSetId(matchingTemplate?.id || editingShift?.shiftSetId || null);
    }
  }, [editingShift, availableUsers, templates]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const usersInSelectedDepartment = useMemo(() => {
    const list = Array.isArray(availableUsers) ? availableUsers : [];
    if (!selectedDepartment) return list;
    return list.filter((u) => {
      const dept = (typeof u?.department === 'string'
        ? u.department
        : (u?.department?.name || u?.department?.title || ''));
      return (dept || '') === selectedDepartment;
    });
  }, [availableUsers, selectedDepartment]);

  const selectedUserIdsSet = useMemo(() => {
    const selected = Array.isArray(formData?.userIds) ? formData.userIds : [];
    return new Set(selected.map((u) => String(u?.id)));
  }, [formData?.userIds]);

  const usersInSelectedDepartmentIds = useMemo(() => {
    return usersInSelectedDepartment.map((u) => String(u?.id)).filter(Boolean);
  }, [usersInSelectedDepartment]);

  const allUsersSelectedInDepartment =
    usersInSelectedDepartmentIds.length > 0 &&
    usersInSelectedDepartmentIds.every((id) => selectedUserIdsSet.has(id));

  const someUsersSelectedInDepartment =
    usersInSelectedDepartmentIds.some((id) => selectedUserIdsSet.has(id)) &&
    !allUsersSelectedInDepartment;

  const toggleSelectAllUsersInDepartment = (checked) => {
    const selected = Array.isArray(formData?.userIds) ? formData.userIds : [];

    if (checked) {
      const byId = new Map();
      for (const u of selected) byId.set(String(u?.id), u);
      for (const u of usersInSelectedDepartment) byId.set(String(u?.id), u);
      handleInputChange('userIds', Array.from(byId.values()).filter((u) => u?.id != null));
      return;
    }

    const removeIds = new Set(usersInSelectedDepartmentIds);
    handleInputChange('userIds', selected.filter((u) => !removeIds.has(String(u?.id))));
  };

  const handleRecurrenceChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      recurrenceRule: {
        ...prev?.recurrenceRule,
        [field]: value
      }
    }));
  };

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      recurrenceRule: {
        ...prev?.recurrenceRule,
        days: prev?.recurrenceRule?.days?.includes(day)
          ? prev?.recurrenceRule?.days?.filter(d => d !== day)
          : [...prev?.recurrenceRule?.days, day]
      }
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);

    try {
      // Validate required fields
      if (!formData.userIds || formData.userIds.length === 0) {
        throw new Error(t('PleaseSelectAtLeastOneUser'));
      }
      if (!formData.date) {
        throw new Error(t('PleaseSelectDate'));
      }
      if (!formData.startTime || !formData.endTime) {
        throw new Error(t('PleaseSelectStartAndEndTimes'));
      }

      // Convert times to minutes
      const startTimeMinutes = timeToMinutes(formData.startTime);
      const endTimeMinutes = timeToMinutes(formData.endTime);
      const breakDurationMinutesRaw = (Number.isFinite(formData.breakDuration) && formData.breakDuration > 0)
        ? formData.breakDuration
        : null;
      const breakStartTimeMinutes = formData.breakStartTime ? timeToMinutes(formData.breakStartTime) : null;
      const breakEndTimeMinutes = formData.breakEndTime ? timeToMinutes(formData.breakEndTime) : null;

      // Validate break window (client-side mirror; backend remains source of truth)
      const overnight = endTimeMinutes <= startTimeMinutes;
      const shiftLen = overnight
        ? (24 * 60 - startTimeMinutes + endTimeMinutes)
        : (endTimeMinutes - startTimeMinutes);

      const toOffset = (minutes) => {
        let off = minutes - startTimeMinutes;
        if (overnight && off < 0) off += 24 * 60;
        return off;
      };

      const hasBreakConfig = breakStartTimeMinutes !== null || breakEndTimeMinutes !== null || breakDurationMinutesRaw !== null;
      if (hasBreakConfig) {
        const startOffset = (breakStartTimeMinutes !== null) ? toOffset(breakStartTimeMinutes) : null;
        const endOffset = (breakEndTimeMinutes !== null) ? toOffset(breakEndTimeMinutes) : null;

        if (startOffset !== null) {
          if ((!overnight && startOffset < 0) || startOffset < 0 || startOffset >= shiftLen) {
            throw new Error('Break start time must be within the shift');
          }
        }

        if (endOffset !== null) {
          if ((!overnight && endOffset < 0) || endOffset <= 0 || endOffset > shiftLen) {
            throw new Error('Break end time must be within the shift');
          }
        }

        if (breakDurationMinutesRaw !== null && breakDurationMinutesRaw > shiftLen) {
          throw new Error('Break duration must fit within the shift');
        }

        if (startOffset !== null && endOffset !== null) {
          if (endOffset <= startOffset) {
            throw new Error('Break end time must be after break start time');
          }
          const windowLen = endOffset - startOffset;
          if (breakDurationMinutesRaw !== null && breakDurationMinutesRaw > windowLen) {
            throw new Error('Break duration must not exceed break window length');
          }
        } else {
          if (breakDurationMinutesRaw !== null) {
            if (startOffset !== null && startOffset + breakDurationMinutesRaw > shiftLen) {
              throw new Error('Break start time and duration must fit within the shift');
            }
            if (endOffset !== null && breakDurationMinutesRaw > endOffset) {
              throw new Error('Break duration must fit within the shift before break end time');
            }
          }
        }
      }

      if (formData.type === 'single') {
        // Create single shift or override for each selected user
        for (const user of formData.userIds) {
          const effectiveId = (editingShift && String(user.id) === String(editingShift.userId))
            ? (editingShift.id || 0)
            : 0;

          const shiftInput = {
            id: effectiveId,
            userId: user.id,
            date: formData.date,
            startTimeMinutes,
            endTimeMinutes,
            breakStartTimeMinutes: breakStartTimeMinutes ?? undefined,
            breakEndTimeMinutes: breakEndTimeMinutes ?? undefined,
            breakDurationMinutes: breakDurationMinutesRaw ?? undefined,
            overrideReason: formData.reason || undefined,
          };

          if (editingShift?.isVirtual) {
            // Converting virtual to manual override
            await createOverride(shiftInput);
          } else {
            // Creating or updating manual shift
            await setScheduledShift(shiftInput);
          }
        }
      } else if (formData.type === 'recurring') {
        // Create recurring rule
        if (formData.recurrenceRule.days.length === 0) {
          throw new Error(t('PleaseSelectAtLeastOneDayForRecurrence'));
        }

        if (!formData.createNewTemplate && !formData.templateId && !originalSetId) {
          throw new Error(t('PleaseSelectRecurringScheduleForRecurringRule'));
        }

        if (formData.createNewTemplate && !formData.newTemplateName) {
          throw new Error(t('PleaseEnterScheduleTitle'));
        }

        // Build RRULE string
        const rruleString = `FREQ=WEEKLY;BYDAY=${formData.recurrenceRule.days.join(',')};INTERVAL=${formData.recurrenceRule.interval || 1}`;

        const selectedUserIds = formData.userIds.map(u => u.id);

        // Determine / create the owning shift set
        // For create flow: rely on selected templateId.
        // For edit flow: fall back to the existing shiftSetId.
        let shiftSetId = 0;
        let newlyCreatedSetId = null; // Track for cleanup if rule creation fails
        if (formData.templateId) shiftSetId = Number(formData.templateId);
        else if (editingShift?.shiftSetId) shiftSetId = Number(editingShift.shiftSetId);
        else if (originalSetId) shiftSetId = Number(originalSetId);

        if (formData.createNewTemplate) {
          const setInput = {
            name: formData.newTemplateName,
            description: formData.newTemplateDescription || '',
            assignedUserIds: selectedUserIds,
            embeddedSingleShifts: [],
            ruleItems: [],
          };
          const createdSet = await createShiftSet(setInput);
          shiftSetId = createdSet?.id;
          newlyCreatedSetId = shiftSetId;
        } else if (shiftSetId) {
          // If the user edited the selected template's title/description, update the shift set.
          // IMPORTANT: ShiftSetInput on the backend defaults EmbeddedSingleShifts/RuleItems to empty lists.
          // If we PUT without sending existing lists, we'd accidentally wipe the template.
          const nameChanged = (formData.newTemplateName || '') !== (originalTemplateName || '');
          const descChanged = (formData.newTemplateDescription || '') !== (originalTemplateDescription || '');

          if (nameChanged || descChanged) {
            const existing = await getShiftSet(shiftSetId);
            const existingEmbedded = (existing?.embeddedSingleShifts || []).map((s) => ({
              id: s?.id,
              startUtc: s?.startUtc,
              endUtc: s?.endUtc,
            })).filter((s) => !!s.startUtc && !!s.endUtc);

            const existingRuleItems = (existing?.ruleItems || []).map((ri) => ({
              id: ri?.id,
              recurringShiftRuleId: ri?.recurringShiftRuleId,
            })).filter((ri) => !!ri.recurringShiftRuleId);

            const setInput = {
              name: (formData.newTemplateName || existing?.name || '').trim(),
              description: (formData.newTemplateDescription ?? existing?.description ?? ''),
              assignedUserIds: selectedUserIds,
              embeddedSingleShifts: existingEmbedded,
              ruleItems: existingRuleItems,
            };
            await updateShiftSet(shiftSetId, setInput);
          } else {
            // Ensure the shift set assignments match the chosen users
            await assignUsersToShiftSet(shiftSetId, selectedUserIds);
          }
        }

        if (!shiftSetId) {
          throw new Error(t('FailedToDetermineShiftSetForRecurringRule'));
        }

        const ruleIdToUpdate = editingShift?.recurringShiftRuleId || originalRuleId || 0;
        const editingExistingRule = Number(ruleIdToUpdate) > 0;
        const ruleInput = {
          id: ruleIdToUpdate || 0,
          shiftSetId,
          // NOTE: timeZoneId is NOT sent - backend determines from shift set/company settings
          rRule: rruleString,
          startTimeOfDayMinutes: startTimeMinutes,
          endTimeOfDayMinutes: endTimeMinutes,
          breakStartTimeOfDayMinutes: breakStartTimeMinutes ?? undefined,
          breakEndTimeOfDayMinutes: breakEndTimeMinutes ?? undefined,
          breakDurationMinutes: breakDurationMinutesRaw ?? undefined,
          startDateUtc: formData.date,
          ...(formData?.recurrenceRule?.endDate ? { endDateUtc: formData.recurrenceRule.endDate } : {}),
          ...(editingExistingRule ? { effectiveDateUtc: formData.date } : {}),
        };

        // Only update when we are actually editing an existing recurring rule.
        // Creating a schedule should always POST a new rule for the selected shift set.
        try {
          if (editingExistingRule) {
            await updateRecurringRule(Number(ruleIdToUpdate), ruleInput);
          } else {
            await createRecurringRule(ruleInput);
          }
        } catch (ruleErr) {
          // If we just created a new ShiftSet and the rule failed (e.g. conflict),
          // clean up the orphaned ShiftSet so it doesn't persist without a rule.
          if (newlyCreatedSetId) {
            try { await deleteShiftSet(newlyCreatedSetId, { cascade: true }); } catch { /* ignore cleanup errors */ }
          }
          throw ruleErr;
        }
      }

      // Success - close drawer and refresh data
      onScheduleCreated?.();
      onClose?.();
      
      // Reset form
      setFormData({
        type: 'single',
        date: '',
        startTime: '',
        endTime: '',
        breakStartTime: '',
        breakEndTime: '',
        breakDuration: null,
        userIds: [],
        templateId: '',
        createNewTemplate: true,
        newTemplateName: '',
        newTemplateDescription: '',
        addRecurrence: false,
        recurrenceRule: {
          frequency: 'WEEKLY',
          days: [],
          interval: 1,
          endDate: ''
        },
        reason: ''
      });

      // Reset edit linkage state as well
      setOriginalRuleId(null);
      setOriginalTemplateName('');
      setOriginalSetId(null);
    } catch (err) {
      console.error('Error creating schedule:', err);
      const msg = formatErrorMessage(err, t('FailedToCreateScheduleTryAgain'));
      setError(msg);

      // Show a detailed dialog with backend error(s) + conflict examples (if provided).
      try {
        const details = extractDetailedError(err);
        setErrorDialogData(details);
        setErrorDialogOpen(true);
      } catch {
        // ignore dialog parsing failures
      }

      notifyError(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const weekDays = [
    { short: 'MO', full: t('Monday') },
    { short: 'TU', full: t('Tuesday') },
    { short: 'WE', full: t('Wednesday') },
    { short: 'TH', full: t('Thursday') },
    { short: 'FR', full: t('Friday') },
    { short: 'SA', full: t('Saturday') },
    { short: 'SU', full: t('Sunday') }
  ];

  const startTimeMinutesPreview = formData?.startTime ? timeToMinutes(formData.startTime) : null;
  const endTimeMinutesPreview = formData?.endTime ? timeToMinutes(formData.endTime) : null;
  const isOvernight =
    typeof startTimeMinutesPreview === 'number' &&
    typeof endTimeMinutesPreview === 'number' &&
    endTimeMinutesPreview <= startTimeMinutesPreview;
  const durationMinutes =
    typeof startTimeMinutesPreview === 'number' && typeof endTimeMinutesPreview === 'number'
      ? ((endTimeMinutesPreview - startTimeMinutesPreview + (isOvernight ? 1440 : 0)))
      : null;
  const durationLabel =
    typeof durationMinutes === 'number'
      ? t('DurationHoursMinutes', {
          hours: Math.floor(durationMinutes / 60),
          minutes: durationMinutes % 60,
        })
      : null;

  const formatConflictDateTime = (value) => {
    if (value == null) return '';

    if (typeof value === 'string') {
      const trimmed = value.trim();
      // Backend already provides local formatted strings like "yyyy-MM-dd HH:mm" — show as dd-mm-yyyy HH:mm.
      if (/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}/.test(trimmed)) return `${isoDateToDisplay(trimmed)} ${trimmed.slice(11, 16)}`;

      const d = new Date(trimmed);
      if (!Number.isNaN(d.getTime())) {
        return appDateTime(d);
      }
      return trimmed;
    }

    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) return '';
      return appDateTime(value);
    }

    return String(value);
  };

  const getUserNameById = (userId) => {
    const found = availableUsers?.find(u => String(u.id) === String(userId));
    return found?.name || '';
  };

  const groupConflictsByUser = (conflicts) => {
    const map = new Map();
    (conflicts || []).forEach((c) => {
      const uid = c?.userId || c?.UserId || '';
      const list = map.get(uid) || [];
      list.push(c);
      map.set(uid, list);
    });
    return Array.from(map.entries());
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="absolute inset-y-0 max-w-2xl w-full bg-card shadow-xl" style={{ insetInlineEnd: 0 }}>
        <div className="h-full flex flex-col">
          <Dialog open={!!errorDialogOpen} onOpenChange={(open) => !open && setErrorDialogOpen(false)}>
            <DialogContent size="lg">
            <DialogHeader><DialogTitle>{t('CouldntSaveSchedule')}</DialogTitle></DialogHeader>
            <DialogBody className="max-h-[60vh] overflow-y-auto">
              <div className="space-y-3">
                <div className="text-sm text-foreground">
                  {errorDialogData?.summary || t('UnexpectedErrorOccurred')}
                </div>

                {Array.isArray(errorDialogData?.descriptions) && errorDialogData.descriptions.length > 1 && (
                  <div className="text-sm text-muted-foreground">
                    <div className="font-medium mb-1">{t('ScheduleDetails')}</div>
                    <ul className="list-disc space-y-1" style={{ paddingInlineStart: '1.25rem' }}>
                      {errorDialogData.descriptions.slice(0, 6).map((d, idx) => (
                        <li key={idx}>{d}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {Array.isArray(errorDialogData?.conflicts) && errorDialogData.conflicts.length > 0 && (
                  <div className="text-sm text-muted-foreground">
                    <div className="font-medium mb-1">{t('ConflictsExamples')}</div>
                    <div className="space-y-3">
                      {groupConflictsByUser(errorDialogData.conflicts).slice(0, 5).map(([userId, userConflicts]) => {
                        const userName = getUserNameById(userId);
                        return (
                          <div key={String(userId)} className="p-3 rounded border border-border bg-muted">
                            <div className="font-medium text-foreground">
                              {userName || userId || t('UnknownUser')}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              {t('ConflictsCount', { count: userConflicts.length })}
                            </div>

                            <div className="space-y-2 mt-2">
                              {userConflicts.slice(0, 3).map((c, idx) => {
                                const tzId = c?.timeZoneId || c?.TimeZoneId;
                                const startRaw = c?.candidateStartLocal || c?.CandidateStartLocal || c?.candidateStartUtc || c?.CandidateStartUtc || c?.startUtc || c?.StartUtc;
                                const endRaw = c?.candidateEndLocal || c?.CandidateEndLocal || c?.candidateEndUtc || c?.CandidateEndUtc || c?.endUtc || c?.EndUtc;
                                const start = formatConflictDateTime(startRaw);
                                const end = formatConflictDateTime(endRaw);

                                const sourceType = c?.sourceType || c?.SourceType;

                                const overlappingIds = c?.overlappingScheduledShiftIds || c?.OverlappingScheduledShiftIds;
                                const virtualOverlaps = c?.overlappingVirtualOccurrences || c?.OverlappingVirtualOccurrences;
                                const conflictingTemplateNamesRaw = c?.conflictingTemplateNames || c?.ConflictingTemplateNames;

                                const virtualList = Array.isArray(virtualOverlaps) ? virtualOverlaps : [];
                                const derivedTemplateNames = virtualList
                                  .map(v => v?.shiftSetName || v?.ShiftSetName)
                                  .filter(Boolean);
                                const templateNames = [
                                  ...(Array.isArray(conflictingTemplateNamesRaw) ? conflictingTemplateNamesRaw : []),
                                  ...derivedTemplateNames,
                                ].filter(Boolean);
                                const uniqueTemplateNames = Array.from(new Set(templateNames.map(n => String(n))));

                                const virtualOccurrenceDetails = virtualList
                                  .map((v) => {
                                    const name = v?.shiftSetName || v?.ShiftSetName;
                                    const origin = v?.origin || v?.Origin;
                                    const vStart = formatConflictDateTime(v?.startLocal || v?.StartLocal || v?.startUtc || v?.StartUtc || v?.Start);
                                    const vEnd = formatConflictDateTime(v?.endLocal || v?.EndLocal || v?.endUtc || v?.EndUtc || v?.End);
                                    return {
                                      name: name ? String(name) : '',
                                      origin: origin ? String(origin) : '',
                                      start: vStart,
                                      end: vEnd,
                                    };
                                  })
                                  .filter((x) => x.name || x.start || x.end);

                                return (
                                  <div key={idx} className="rounded border border-border bg-card px-3 py-2">
                                    <div className="text-xs text-foreground">
                                      <span className="font-medium">{t('Time')}:</span> {start}{end ? ` → ${end}` : ''}{tzId ? ` (${tzId})` : ''}
                                    </div>

                                    {sourceType && (
                                      <div className="text-xs text-muted-foreground mt-1">
                                        <span className="font-medium">{t('Source', { defaultValue: 'Source' })}:</span> {String(sourceType)}
                                      </div>
                                    )}

                                    {uniqueTemplateNames.length > 0 && (
                                      <div className="text-xs text-muted-foreground mt-1">
                                        <span className="font-medium">{t('ConflictsWithRecurringSchedules', { defaultValue: 'Conflicts with recurring schedule(s)' })}:</span>{' '}
                                        {uniqueTemplateNames.slice(0, 3).join(', ')}{uniqueTemplateNames.length > 3 ? '…' : ''}
                                      </div>
                                    )}

                                    {virtualOccurrenceDetails.length > 0 && (
                                      <div className="text-xs text-muted-foreground mt-1">
                                        <div className="font-medium">{t('ConflictingOccurrences', { defaultValue: 'Conflicting occurrence(s)' })}:</div>
                                        <ul className="list-disc space-y-1" style={{ paddingInlineStart: '1.25rem' }}>
                                          {virtualOccurrenceDetails.slice(0, 3).map((v, vi) => (
                                            <li key={vi}>
                                              {v.name}{v.origin ? ` (${v.origin})` : ''}{v.start || v.end ? `: ${v.start}${v.end ? ` → ${v.end}` : ''}` : ''}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}

                                    {Array.isArray(overlappingIds) && overlappingIds.length > 0 && (
                                      <div className="text-xs text-muted-foreground mt-1">
                                        <span className="font-medium">{t('ConflictsWithScheduledShifts', { defaultValue: 'Conflicts with scheduled shift(s)' })}:</span>{' '}
                                        {overlappingIds.slice(0, 8).join(', ')}{overlappingIds.length > 8 ? '…' : ''}
                                      </div>
                                    )}

                                    {Array.isArray(virtualOverlaps) && virtualOverlaps.length > 0 && (
                                      <div className="text-xs text-muted-foreground mt-1">
                                        <span className="font-medium">{t('OverlappingVirtualOccurrencesCount', { count: virtualOverlaps.length })}</span>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>

                            {userConflicts.length > 3 && (
                              <div className="text-xs text-muted-foreground mt-2">
                                {t('AndMore', { count: userConflicts.length - 3 })}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {errorDialogData.conflicts.length > 0 && groupConflictsByUser(errorDialogData.conflicts).length > 5 && (
                        <div className="text-xs text-muted-foreground">
                          {t('AndMoreUsers', { count: groupConflictsByUser(errorDialogData.conflicts).length - 5 })}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {(!errorDialogData?.descriptions?.length && !errorDialogData?.conflicts?.length) && (
                  <div className="text-xs text-muted-foreground">
                    {t('NoAdditionalErrorDetailsProvided')}
                  </div>
                )}
              </div>
            </DialogBody>
            <DialogFooter>
              <Button onClick={() => setErrorDialogOpen(false)}>{t('Close')}</Button>
            </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Header */}
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <h2 className="text-xl font-semibold text-foreground">
              {editingShift ? t('EditScheduledShift') : t('CreateScheduledShift')}
            </h2>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
          </div>

          {/* Form Content */}
          <form id="create-schedule-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              {/* Error Message */}
              {error && (
                <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Icon name="AlertCircle" size={20} className="text-destructive" />
                    <p className="text-sm text-destructive">{error}</p>
                  </div>
                </div>
              )}
              {/* Shift Type Selection */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  {t('ShiftType')}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handleInputChange('type', 'single')}
                    className={`p-4 border-2 rounded-lg text-center transition-colors ${
                      formData?.type === 'single' ?'border-primary bg-primary/5 text-primary' :'border-border text-muted-foreground hover:border-border'
                    }`}
                  >
                    <Icon name="Calendar" size={24} className="mx-auto mb-2" />
                    <div className="font-medium">{t('SingleShift')}</div>
                    <div className="text-xs mt-1 opacity-75">{t('OneTimeOccurrence')}</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInputChange('type', 'recurring')}
                    className={`p-4 border-2 rounded-lg text-center transition-colors ${
                      formData?.type === 'recurring' ?'border-primary bg-primary/5 text-primary' :'border-border text-muted-foreground hover:border-border'
                    }`}
                  >
                    <Icon name="Repeat" size={24} className="mx-auto mb-2" />
                    <div className="font-medium">{t('RecurringShift')}</div>
                    <div className="text-xs mt-1 opacity-75">{t('RepeatingPattern')}</div>
                  </button>
                </div>
              </div>

              {/* Recurring Schedule (recurring only) */}
              {formData?.type === 'recurring' && (
                <div className="p-4 bg-muted rounded-lg space-y-4">
                  <h3 className="font-medium text-foreground flex items-center">
                    <Icon name="Grid" size={18} className="me-2" />
                    {t('RecurringSchedule')}
                  </h3>

                  <Checkbox
                    checked={!!formData?.createNewTemplate}
                    onChange={(e) => {
                      const checked = !!e?.target?.checked;
                      handleInputChange('createNewTemplate', checked);
                      if (checked) handleInputChange('templateId', '');
                    }}
                    label={t('CreateNewRecurringSchedule')}
                  />

                  {formData?.createNewTemplate ? (
                    <div className="space-y-3">
                      <Input
                        label={t('ScheduleTitle')}
                        required
                        placeholder={t('ScheduleTitle')}
                        value={formData?.newTemplateName}
                        onChange={(e) => handleInputChange('newTemplateName', e?.target?.value)}
                      />
                      <textarea
                        placeholder={t('ScheduleDescription')}
                        value={formData?.newTemplateDescription}
                        onChange={(e) => handleInputChange('newTemplateDescription', e?.target?.value)}
                        rows="2"
                        className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50"
                      />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Select
                        label={t('RecurringSchedule')}
                        required
                        placeholder={t('SelectRecurringSchedule', { defaultValue: 'Select recurring schedule' })}
                        options={[
                          ...(templates || []).map((template) => ({
                            label: template?.name,
                            value: String(template?.id),
                          })),
                        ]}
                        value={formData?.templateId || ''}
                        onChange={(val) => {
                          handleInputChange('templateId', val);
                          const selected = templates?.find((tmp) => String(tmp?.id) === String(val));
                          if (selected) applyTemplateToForm(selected);
                        }}
                      />

                      {/* Editable title/description for the selected template */}
                      <Input
                        label={t('ScheduleTitle')}
                        required
                        placeholder={t('ScheduleTitle')}
                        value={formData?.newTemplateName}
                        onChange={(e) => handleInputChange('newTemplateName', e?.target?.value)}
                        disabled={!formData?.templateId}
                      />
                      <textarea
                        placeholder={t('ScheduleDescription')}
                        value={formData?.newTemplateDescription}
                        onChange={(e) => handleInputChange('newTemplateDescription', e?.target?.value)}
                        rows="2"
                        className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={!formData?.templateId}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Date Selection */}
              <div className={"grid gap-4 " + (formData?.type === 'recurring' ? 'grid-cols-2' : 'grid-cols-1')}>
                <DateField
                  label={formData?.type === 'single' ? t('Date') : t('StartDate')}
                  required
                  value={formData?.date}
                  onChange={(v) => handleInputChange('date', v)}
                />
                {/* End Date */}
                {formData?.type === 'recurring' && (
                  <DateField
                    label={t('EndDateOptional')}
                    value={formData?.recurrenceRule?.endDate}
                    onChange={(v) => handleRecurrenceChange('endDate', v)}
                  />
                )}
              </div>

              {/* Time Selection */}
              <div className="grid grid-cols-2 gap-4">
                <TimeField
                  label={t('StartTime')}
                  required
                  value={formData?.startTime}
                  onChange={(v) => handleInputChange('startTime', v)}
                />
                <div>
                  <TimeField
                    label={t('EndTime')}
                    required
                    value={formData?.endTime}
                    onChange={(v) => handleInputChange('endTime', v)}
                  />
                  {(isOvernight || durationLabel) && (
                    <div className="mt-1 text-xs text-muted-foreground">
                      {isOvernight ? t('OvernightShiftEndsNextDay') : null}
                      {durationLabel ? ` ${t('DurationLabel', { duration: durationLabel })}` : null}
                    </div>
                  )}
                </div>
              </div>

              {/* Break Duration */}
              <Input
                type="number"
                label={t('BreakDurationMinutes')}
                value={formData?.breakDuration ?? ''}
                onChange={(e) => {
                  const v = e?.target?.value;
                  handleInputChange('breakDuration', v === '' ? null : parseInt(v, 10));
                }}
                min="0"
                step="15"
              />

              {/* Break Start/End Time */}
              <div className="grid grid-cols-2 gap-4">
                <TimeField
                  label={t('BreakStartTime', { defaultValue: 'Break Start Time' })}
                  value={formData?.breakStartTime}
                  onChange={(v) => handleInputChange('breakStartTime', v)}
                />
                <TimeField
                  label={t('BreakEndTime', { defaultValue: 'Break End Time' })}
                  value={formData?.breakEndTime}
                  onChange={(v) => handleInputChange('breakEndTime', v)}
                />
              </div>

              {/* User Selection - Multi-select */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t('AssignToUsers')} {loadingUsers && <span className="text-xs text-muted-foreground">({t('Loading')})</span>}
                  <span className="text-destructive ms-1">*</span>
                </label>

                {/* Department filter */}
                <div className="mb-2">
                  <Select
                    label={t('Department')}
                    placeholder={t('All departments')}
                    options={departmentOptions.map((dept) => ({
                      label: dept,
                      value: dept,
                    }))}
                    value={selectedDepartment || ''}
                    onChange={(val) => setSelectedDepartment(val || '')}
                    disabled={loadingUsers}
                    clearable
                  />
                </div>

                {/* Select all checkbox */}
                <div className="mb-2">
                  <Checkbox
                    size="sm"
                    label={t('SelectAll', { defaultValue: 'Select all' })}
                    checked={allUsersSelectedInDepartment}
                    indeterminate={someUsersSelectedInDepartment}
                    onChange={(e) => toggleSelectAllUsersInDepartment(!!e?.target?.checked)}
                    disabled={loadingUsers || usersInSelectedDepartmentIds.length === 0}
                  />
                </div>

                <div className="flex gap-2 items-start">
                  <Select
                    multiple
                    searchable
                    className="flex-1"
                    options={filteredUserOptions.map(u => {
                      const deptLabel = typeof u?.department === 'string' ? u.department : (u?.department?.name || u?.department?.title || '');
                      return {
                        label: `${u.name || u.fullName}${deptLabel ? ` - ${deptLabel}` : ''}`,
                        value: u.id
                      };
                    })}
                    value={(formData?.userIds || []).map(u => u.id)}
                    onChange={(ids) => {
                      const optionMap = new Map(filteredUserOptions.map(u => [u.id, u]));
                      const currentMap = new Map((formData?.userIds || []).map(u => [u.id, u]));
                      const selected = (ids || []).map(id =>
                        optionMap.get(id) || currentMap.get(id)
                      ).filter(Boolean);
                      handleInputChange('userIds', selected);
                    }}
                    placeholder={t('SelectUsers')}
                    loading={loadingUsers}
                    disabled={loadingUsers}
                  />
                  {formData?.userIds?.length > 0 && (
                    <Button variant="ghost" size="icon" className="mt-0.5" onClick={() => handleInputChange('userIds', [])} aria-label={t('ClearUsers')}>
                      <Icon name="X" size={16} />
                    </Button>
                  )}
                </div>
                {(!formData?.userIds || formData?.userIds?.length === 0) && <p className="text-xs text-destructive mt-1">{t('AtLeastOneUserIsRequired')}</p>}
                {formData?.userIds?.length > 0 && <p className="text-xs text-muted-foreground mt-1">{t('UsersSelectedCount', { count: formData?.userIds?.length })}</p>}

                {availableUsers.length === 0 && !loadingUsers && (
                  <p className="mt-1 text-xs text-warning">
                    {t('NoUsersAvailable')}
                  </p>
                )}
              </div>


              {/* Recurrence Options (only for recurring type) */}
              {formData?.type === 'recurring' && (
                <div className="p-4 bg-muted rounded-lg space-y-4">
                  <h3 className="font-medium text-foreground flex items-center">
                    <Icon name="Repeat" size={18} className="me-2" />
                    {t('RecurrencePattern')}
                  </h3>

                  {/* Days Selection */}
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-2">
                      {t('RepeatOnDays')}
                      <span className="text-destructive ms-1">*</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {weekDays?.map((day) => (
                        <button
                          key={day?.short}
                          type="button"
                          onClick={() => toggleDay(day?.short)}
                          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            formData?.recurrenceRule?.days?.includes(day?.short)
                              ? 'bg-primary text-primary-foreground' :'bg-card border border-border text-muted-foreground hover:bg-muted'
                          }`}
                          title={day?.full}
                        >
                          {day?.short}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Week Interval */}
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-2">
                      {t('RepeatEvery', { defaultValue: 'Repeat every' })}
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4].map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => handleRecurrenceChange('interval', n)}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                              formData?.recurrenceRule?.interval === n
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-card border border-border text-muted-foreground hover:bg-muted'
                            }`}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {formData?.recurrenceRule?.interval === 1
                          ? t('WeekSingular', { defaultValue: 'week' })
                          : t('WeekPlural', { defaultValue: 'weeks' })}
                      </span>
                    </div>
                    {formData?.recurrenceRule?.interval > 1 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('AlternatingWeeksDescription', {
                          defaultValue: 'Selected days will repeat every {{count}} weeks',
                          count: formData?.recurrenceRule?.interval,
                        })}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Preview Summary */}
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-medium text-foreground mb-2">{t('Summary')}</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('Type')}:</span>
                    <span className="font-medium">{formData?.type === 'single' ? t('SingleShift') : t('RecurringShift')}</span>
                  </div>
                  {formData?.date && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('Date')}:</span>
                      <span className="font-medium">{formData?.date}</span>
                    </div>
                  )}
                  {formData?.startTime && formData?.endTime && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('Time')}:</span>
                      <span className="font-medium">{formData?.startTime} - {formData?.endTime}</span>
                    </div>
                  )}
                  {formData?.userIds && formData?.userIds?.length > 0 && (
                    <div className="flex justify-between items-start">
                      <span className="text-muted-foreground">{t('AssignedTo')}:</span>
                      <span className="font-medium text-end flex-1 ms-2">
                        {formData?.userIds?.map(u => u?.name || u?.fullName).join(', ')}
                      </span>
                    </div>
                  )}
                  {formData?.type === 'recurring' && formData?.recurrenceRule?.days?.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('Days')}:</span>
                      <span className="font-medium">
                        {formData?.recurrenceRule?.interval > 1
                          ? t('EveryNWeeksOn', {
                              defaultValue: 'Every {{count}} weeks on {{days}}',
                              count: formData?.recurrenceRule?.interval,
                              days: formData?.recurrenceRule?.days?.join(', '),
                            })
                          : formData?.recurrenceRule?.days?.join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>

          {/* Footer Actions */}
          <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              {t('Cancel')}
            </Button>
            <Button
              type="submit"
              form="create-schedule-form"
              disabled={submitting}
              loading={submitting}
            >
              {(() => {
                const isEditingTemplate = formData?.type === 'recurring' && !formData?.createNewTemplate && !!formData?.templateId;
                const isUpdating = !!editingShift || isEditingTemplate || Number(originalRuleId) > 0;
                return isUpdating ? t('UpdateSchedule') : t('CreateSchedule');
              })()}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateScheduleDrawer;